import * as React from "react";
import "@styles/synchrony-buttons-selectors-theme.scss";
import { defaultIconStyle } from "@styles/synchrony-core-component-default";
// utilities
import { gaTag } from "@utils/browser-helpers";

type ButtonTheme = "primary" | "secondary" | "tertiary" | "cancel" | "success" | "disabled";

interface ButtonComponentProps {
  className?: string;
  componentID: string;
  width?: string;
  height?: string;
  icon?: React.ReactNode;
  iconStyle?: string;
  userInputStyle?: React.CSSProperties;
  label: string;
  toUpperCase?: boolean;
  dataTestID?: string;
  ariaLabel?: string;
  disabled?: boolean;
  theme?: ButtonTheme;
  onClickCallback?: React.MouseEventHandler<HTMLButtonElement>;
  customGATag?: string;
  customStyle?: string | Record<string, any>;
  type?: "submit" | "button" | "reset";
}

/**
 * Base + hover classes for each theme, expressed entirely in Tailwind so hover
 * state is managed by CSS instead of React state. The `invert` styles that
 * previously fired on mouseEnter are now `hover:` variants of the same colors.
 * `disabled` intentionally has no hover state.
 */
const themeClasses: Record<ButtonTheme, string> = {
  primary:
    "bg-color-teal text-white hover:text-color-teal hover:bg-white",
  secondary:
    "bg-color-light-teal text-color-teal border-solid border-color-teal border-2 hover:text-color-light-teal hover:bg-color-teal",
  tertiary:
    "bg-color-mari-gold text-charcoal-gray hover:text-color-mari-gold hover:bg-charcoal-gray",
  cancel:
    "bg-white text-color-teal hover:text-white hover:bg-color-teal",
  success:
    "bg-color-dark-green text-charcoal-gray hover:text-color-dark-green hover:bg-charcoal-gray",
  disabled:
    "bg-color-white-switch text-color-steel-gray border-solid border-color-steel-gray border-2 cursor-not-allowed",
};

const ButtonComponent: React.FC<ButtonComponentProps> = ({
  className = "",
  componentID,
  width = "w-33",
  height = "h-12",
  icon,
  iconStyle = defaultIconStyle,
  userInputStyle = {},
  label = "",
  toUpperCase = true,
  dataTestID,
  ariaLabel = label,
  disabled = false,
  theme = "primary",
  onClickCallback,
  customGATag,
  customStyle = "",
  type = "submit",
}) => {
  const activeTheme: ButtonTheme = disabled ? "disabled" : theme;
  const buttonStyle = themeClasses[activeTheme];

  const buildCustomStyle = (): string => {
    if (typeof customStyle === "string") {
      return customStyle;
    } else if (typeof customStyle === "object") {
      return Object.entries(customStyle)
        .map(([key, value]) => `${key}: ${value};`)
        .join(" ");
    }
    return "";
  };

  return (
    <button
      aria-label={ariaLabel}
      aria-required="true"
      type={type}
      id={componentID}
      data-testid={dataTestID || componentID}
      data-ga_click_id={customGATag || componentID}
      data-ga_tag={gaTag()}
      onClick={onClickCallback}
      disabled={disabled}
      style={userInputStyle}
      className={`${className} ${disabled ? "" : "cursor-pointer"} ${
        icon && iconStyle
      } ${buttonStyle}  ${buildCustomStyle()} ${height} ${width}
        `}
    >
      {icon} {toUpperCase ? label.toUpperCase() : label}
    </button>
  );
};

export default ButtonComponent;
